import { ApiQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { AsyncComponent, Spinner } from '@frontend/components/common';
import { MultiUserSelect } from '@frontend/user/select';
import React from 'react';

const AccountSelectV2 = React.lazy(() => import('@frontend/account/select').then((module) => ({ default: module.AccountSelectV2 })));
const BrandingSelectV2 = React.lazy(() => import('@frontend/account/select').then((module) => ({ default: module.BrandingSelectV2 })));
const WorkflowSelectV2 = React.lazy(() => import('@frontend/workflow/select').then((module) => ({ default: module.WorkflowSelectV2 })));
const IoTSelectV2 = React.lazy(() => import('@frontend/iot/select').then((module) => ({ default: module.IoTSelectV2 })));
const ScheduleSelect = React.lazy(() => import('@frontend/report/select').then((module) => ({ default: module.ScheduleSelect })));
const ReportSelect = React.lazy(() => import('@frontend/report/select').then((module) => ({ default: module.ReportSelect })));
const SpotSelectV2 = React.lazy(() => import('@frontend/spot/select').then((module) => ({ default: module.SpotSelectV2 })));
const ModuleSelectV2 = React.lazy(() => import('@frontend/module/select').then((module) => ({ default: module.ModuleSelectV2 })));
const ModuleTemplateSelectV2 = React.lazy(() => import('@frontend/module/select').then((module) => ({ default: module.ModuleTemplateSelectV2 })));
const DeviceSelectV2 = React.lazy(() => import('@frontend/device/select').then((module) => ({ default: module.DeviceSelectV2 })));
const PlaylistSelectV2 = React.lazy(() => import('@frontend/playlist/select').then((module) => ({ default: module.PlaylistSelectV2 })));
const ProblemSelectV2 = React.lazy(() => import('@frontend/problem/select').then((module) => ({ default: module.ProblemSelectV2 })));
const UserSelectV2 = React.lazy(() => import('@frontend/user/select').then((module) => ({ default: module.UserSelectV2 })));
const AttributeSelectV2 = React.lazy(() => import('@frontend/attribute/select').then((module) => ({ default: module.AttributeSelectV2 })));
const GroupSelectV2 = React.lazy(() => import('@frontend/group/select').then((module) => ({ default: module.GroupSelectV2 })));
const BudgetSelectV2 = React.lazy(() => import('@frontend/budget/select').then((module) => ({ default: module.BudgetSelectV2 })));
const SlotSelectV2 = React.lazy(() => import('@frontend/slot/select').then((module) => ({ default: module.SlotSelectV2 })));
const SlotTemplateSelectV2 = React.lazy(() => import('@frontend/slot/select').then((module) => ({ default: module.SlotTemplateSelectV2 })));
const QuestionSelectV2 = React.lazy(() => import('@frontend/question/select').then((module) => ({ default: module.QuestionSelectV2 })));
const ProductSelectV2 = React.lazy(() => import('@frontend/product/select').then((module) => ({ default: module.ProductSelectV2 })));
const UserInterfaceSelectV2 = React.lazy(() => import('@frontend/user-interface/select').then((module) => ({ default: module.UserInterfaceSelectV2 })));
const TransactionSelectV2 = React.lazy(() => import('@frontend/transaction/select').then((module) => ({ default: module.TransactionSelectV2 })));
const EquipmentSelectV2 = React.lazy(() => import('@frontend/equipment/select').then((module) => ({ default: module.EquipmentSelectV2 })));
const AttributeValueSelectV2 = React.lazy(() => import('@frontend/attribute/select').then((module) => ({ default: module.AttributeValueSelectV2 })));
const CertificateSelectV2 = React.lazy(() => import('@frontend/certificate/select').then((module) => ({ default: module.CertificateSelectV2 })));
const CostCenterSelectV2 = React.lazy(() => import('@frontend/cost-center/select').then((module) => ({ default: module.CostCenterSelectV2 })));
const WizardSelect = React.lazy(() => import('@frontend/wizard/select').then((module) => ({ default: module.WizardSelect })));
const KitProductSelect = React.lazy(() => import('@frontend/product/select').then((module) => ({ default: module.KitProductSelect })));

interface Props<S> {
    id: string;
    objectKey: keyof S | string;
    submitted: boolean;
    required?: boolean;
    disabled?: boolean;
    useConditionedStyling?: boolean;
    isClearable?: boolean;
    label?: React.ReactNode;
    value?: string | null;
    onChange?: (value: string | string[] | null) => void;
    fieldProps?: Map<keyof S, any>;
    queryParams?: ApiQueryParams<string | number>;
    className?: string;
    allowURLChange?: boolean;
    queryKeyOverride?: string;
    defaultValue?: string | null;
    hideLabel?: boolean;
    onBlur?: () => void;
    hidePlaceholder?: boolean;
}

const fallback = (
    <div>
        <Spinner />
    </div>
);
export const ObjectSelectMapper = <S,>(props: Props<S>) => {
    switch (props.objectKey) {
        case 'copy_account_id':
        case 'account_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <AccountSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'branding_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <BrandingSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'workflow_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <WorkflowSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'question_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <QuestionSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'product_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <ProductSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'kit_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <KitProductSelect
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'product_category_id':
        case 'default_product_category_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <GroupSelectV2
                        {...props}
                        {...props.fieldProps}
                        type={EntityType.PRODUCT_CATEGORY}
                    />
                </AsyncComponent>
            );
        case 'product_group_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <GroupSelectV2
                        {...props}
                        {...props.fieldProps}
                        type={EntityType.PRODUCT_GROUP}
                    />
                </AsyncComponent>
            );
        case 'equipment_group_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <GroupSelectV2
                        {...props}
                        {...props.fieldProps}
                        type={EntityType.EQUIPMENT_GROUP}
                    />
                </AsyncComponent>
            );
        case 'questionnaire_id':
        case 'consume_questionnaire_id':
        case 'return_questionnaire_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <GroupSelectV2
                        {...props}
                        {...props.fieldProps}
                        type={EntityType.QUESTIONNAIRE}
                    />
                </AsyncComponent>
            );
        case 'group_id':
        case 'user_group_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <GroupSelectV2
                        {...props}
                        {...props.fieldProps}
                        type={EntityType.USER_GROUP}
                    />
                </AsyncComponent>
            );
        case 'report_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <ReportSelect
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'schedule_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <ScheduleSelect
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'current_spot_id':
        case 'spot_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <SpotSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'current_module_id':
        case 'module_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <ModuleSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'module_template_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <ModuleTemplateSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'current_slot_id':
        case 'slot_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <SlotSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'slot_template_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <SlotTemplateSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'iot_id':
        case 'twin_iot_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <IoTSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'device_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <DeviceSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );

        case 'playlist_id':
        case 'default_playlist_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <PlaylistSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'user_interface_id':
        case 'next_user_interface_id':
        case 'default_user_interface_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <UserInterfaceSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'problem_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <ProblemSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'user_id':
        case 'owner_id':
        case 'sender_id':
        case 'receiver_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <UserSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'senders':
        case 'receivers':
            return (
                <AsyncComponent fallback={fallback}>
                    <MultiUserSelect
                        {...props}
                        {...props.fieldProps}
                        value={[]}
                    />
                </AsyncComponent>
            );
        case 'attribute_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <AttributeSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'budget_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <BudgetSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'certificate_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <CertificateSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'transaction_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <TransactionSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'equipment_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <EquipmentSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'value_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <AttributeValueSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'printer_page_rotate':
            return (
                <AsyncComponent fallback={fallback}>
                    <AttributeValueSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'cost_center_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <CostCenterSelectV2
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
        case 'wizard_id':
            return (
                <AsyncComponent fallback={fallback}>
                    <WizardSelect
                        {...props}
                        {...props.fieldProps}
                    />
                </AsyncComponent>
            );
    }
    return <></>;
};
